import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { BackendTypes, HttpResponse } from '@tf/api';
import { BackendClient, PublicApiClient } from '@tf/api';
import { TFNotifier } from '@tf/ui';

import type { LocalSchemaItem, PublicSchemaItem, SchemaResolveResponse } from '@/core/types/schemas';

const usersInvitationsQueryOptions = () => {
	return queryOptions({
		queryKey: ['invitations'],
		queryFn: async (): Promise<BackendTypes.GetUsersInvitationsData> => {
			const api = BackendClient.getInstance();
			const res = await api.getUsersInvitations();
			return res.json();
		},
		staleTime: 0,
	});
};

export const useUsersInvitations = () => {
	return useQuery(usersInvitationsQueryOptions());
};

export const useRevokeUserInvitationMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({
			email,
		}: {
			email: string;
		}): Promise<BackendTypes.RevokeUsersInvitationData> => {
			await BackendClient.getInstance().revokeUsersInvitation({ email });
		},
		onSuccess: () => {
			return queryClient.invalidateQueries(usersInvitationsQueryOptions());
		},
		onError: () => {
			TFNotifier.error('Failed to revoke invitation. Please try again');
		},
	});
};

const usersQueryOptions = () => {
	return queryOptions({
		queryKey: ['users'],
		queryFn: async (): Promise<BackendTypes.GetUsersData> => {
			const res = await BackendClient.getInstance().getUsers();
			return res.json();
		},
		staleTime: 20 * 1000,
	});
};

export const useUsers = () => {
	return useQuery(usersQueryOptions());
};

export const useUserInvitationMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (
			invites: BackendTypes.UserInvitation[]
		): Promise<BackendTypes.CreateUsersInvitationData> => {
			const api = BackendClient.getInstance();

			await Promise.all(invites.map((invite) => api.createUsersInvitation(invite)));
		},
		onSuccess: async () => {
			await Promise.all([
				queryClient.refetchQueries({ queryKey: ['invitations'] }),
				queryClient.refetchQueries({ queryKey: ['overviewAccounts'] }),
			]);
		},
	});
};

export const useUpdateUserMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			id,
			data,
		}: {
			id: string;
			data: BackendTypes.UserUpdate;
		}): Promise<BackendTypes.UpdateUserData> => {
			await BackendClient.getInstance().updateUser(id, data);
		},
		onSuccess: () => {
			return queryClient.refetchQueries({ queryKey: ['users'] });
		},
	});
};

const tenantCapabilitiesQueryOptions = () => {
	const CAPABILITIES_PATH = 'types/general/role_capabilities_user';
	return queryOptions({
		queryKey: ['capabilitiesFromSchemas', 'all'],
		queryFn: async (): Promise<Array<string>> => {
			const api = PublicApiClient.getInstance();

			const resolveResp: HttpResponse<SchemaResolveResponse[]> = await api.request({
				path: `/sl/schemas/public/schema/resolve`,
				method: 'POST',
				body: [CAPABILITIES_PATH],
			});

			const resolved: SchemaResolveResponse[] = await resolveResp.json();

			const ids = resolved
				.find((req) => req.initial_request === CAPABILITIES_PATH)
				?.resolved_schemas.map(({ id }) => id);

			if (!ids) {
				throw new Error('Failed to resolve schema');
			}

			const allCapabilitiesArray = await Promise.all(
				ids.map(async (id) => {
					const schemaResp = await api.request({
						path: `/sl/schemas/public/schema_by_uri?uri=${encodeURIComponent(id)}`,
						method: 'GET',
					});

					const schema: LocalSchemaItem | PublicSchemaItem = await schemaResp.json();

					// check for capabilities in tenant schema
					if ('allOf' in schema.json_schema) {
						const defs = schema.json_schema.allOf.filter((c) => '$defs' in c) ?? [];
						const capabilitiesObjects = defs.reduce((acc, item) => {
							if ('$defs' in item) {
								return { ...acc, ...item.$defs };
							}
							return acc;
						}, {} as Record<string, { const: string; $anchor: string }>);

						return Object.values(capabilitiesObjects).map((value) => value.const);
					}

					// check for capabilities in public schema
					if ('$defs' in schema.json_schema) {
						return Object.values(schema.json_schema['$defs']).map((value) => value.const);
					}

					return [];
				})
			);

			return allCapabilitiesArray.flat();
		},
		staleTime: Infinity,
	});
};

export const useTenantCapabilities = () => {
	return useQuery(tenantCapabilitiesQueryOptions());
};
